import React, { forwardRef, useRef, useCallback } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { ComplianceRequirement } from 'types/compliance';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useIsomorphicLayoutEffect } from 'types/react';
import { getHighestComplianceRequirement } from 'utils/compliance';
import { listingPageModalIds } from 'constants/test-constants';
import { ADDRESS_MODAL, useModal } from 'contexts/modal';
import { ChevronIcon } from '@core/ui/navigation/components/desktop/internal/ChevronIcon';
import {
  EventRepeatIcon,
  HomeWorkIcon,
  TrendingUpIcon,
} from 'components/atomic/atoms/icons';
import PillCtaButton from 'components/atomic/atoms/pill-cta-button';
import {
  usePriceHistoryTimeline,
  type PriceHistoryTimelineRecordView,
  type PriceHistoryTimelineMonthGroupView,
} from './use-price-history-timeline';
import { PriceHistoryCard } from './price-history-card';
import {
  usePriceHistoryRecordNavigation,
  type PriceHistoryViewListingHandler,
} from './price-history-navigation';
import type { PriceHistoryListingData } from 'domain/listing/value-objects';
import styles from './price-history-timeline.module.scss';
import { StandardStatus } from '@zoocasa/go-search';
import { isActive } from 'types/reso';

const COMPLIANCE_VISIBLE_ENTRY_COUNT = 2 as const;
const TODAY_GROUP_VISIBLE_COUNT = 1 as const;
const BOTTOM_EXPAND_BUTTON_ICON_SIZE = 16;
const BOTTOM_EXPAND_BUTTON_SX = {
  '&.MuiButton-fullWidth': { width: 'auto' },
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
  columnGap: '4px',
  fontSize: '14px',
  fontWeight: 400,
  minWidth: 'unset',
  px: '14px',
  py: '10px',
  width: 'auto',
} as const;

export type PriceHistoryTimelineRecordProps = PriceHistoryListingData & {
  providerId: number;
  complianceRequirement?: ComplianceRequirement;
};

export interface PriceHistoryTimelineProps<T extends PriceHistoryTimelineRecordProps = PriceHistoryTimelineRecordProps> {
  currentListingId: number;
  isRental: boolean;
  saleHistory?: readonly T[];
  rentalHistory?: readonly T[];
  currentListingData: T;
  onComplianceRequirementClick?: (complianceRequirement: ComplianceRequirement, providerId: number) => void;
  onViewListingClick?: PriceHistoryViewListingHandler;
}

// Compliance disclaimer shown when login or terms acceptance is required to reveal more history.
function ComplianceRequirementMessage({ complianceRequirement, onClick }: { complianceRequirement: ComplianceRequirement; onClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);

  if (complianceRequirement === ComplianceRequirement.SIGN_IN) {
    return (
      <div className={styles.complianceMessage}>
        <Trans
          t={t}
          i18nKey={LOCALE_COMMON_PATHS.COMPLIANCE_PRICE_HISTORY_SIGN_IN_REQUIRED}
          components={{ span: <span onClick={onClick} /> }}
          defaultValue={'Real estate boards require that you sign in to see the full details of this property. <span> Log in or sign up </span> to see price history.'}
        />
      </div>
    );
  }

  if (complianceRequirement === ComplianceRequirement.TERMS_OF_USE) {
    return (
      <div className={styles.complianceMessage}>
        <Trans
          t={t}
          i18nKey={LOCALE_COMMON_PATHS.COMPLIANCE_PRICE_HISTORY_TERMS_OF_USE_REQUIRED}
          components={{ span: <span onClick={onClick} /> }}
          defaultValue={'Real estate boards require that you accept Terms of use to see the full details of this property. <span>Accept Terms of Use </span> to see price history.'}
        />
      </div>
    );
  }

  return null;
}

// Header component for the section title and top-right chevron expand control.
function TimelineHeader({
  isExpanded,
  showExpandControls,
  onToggleExpanded,
}: {
  isExpanded: boolean;
  showExpandControls: boolean;
  onToggleExpanded: () => void;
}) {
  const { t } = useTranslation(Namespaces.COMMON);

  return (
    <div className={styles.header}>
      <h2 className={styles.heading} data-testid={listingPageModalIds.priceHistoryHeading}>
        {/* Decorative icon mirrors sticky tabs asset */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/assets/images/sticky-tabs/price-history.png"
          alt=""
          aria-hidden="true"
          className={styles.headingIcon}
        />
        {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_HEADING_LABEL, { defaultValue: 'Price History' })}
      </h2>
      {showExpandControls && (
        <button
          type="button"
          className={styles.chevronButton}
          onClick={onToggleExpanded}
          aria-label={isExpanded
            ? t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_LESS_BUTTON_LABEL, { defaultValue: 'View Less' })
            : t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_MORE_BUTTON_LABEL, { defaultValue: 'View More' })}
        >
          <ChevronIcon width={24} height={24} isExpanded={isExpanded} className={styles.chevronIcon} />
        </button>
      )}
    </div>
  );
}

// Stat card component used for last sold price, appreciation, and avg. days listed.
function TimelineStats({
  stats,
  isComplianceBlocked,
  placeholder,
}: {
  stats: ReturnType<typeof usePriceHistoryTimeline>['stats'];
  isComplianceBlocked: boolean;
  placeholder: string;
}) {
  const { t } = useTranslation(Namespaces.COMMON);

  return (
    <div className={styles.statsGrid}>
      <article className={styles.statCard}>
        <div className={styles.statContent}>
          <p className={styles.statLabel}>
            {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_STAT_LAST_SOLD_PRICE, { defaultValue: 'Last Sold Price' })}
          </p>
          <p className={styles.statValue}>
            <span className={styles.lastSoldPriceDefault}>
              {isComplianceBlocked ? placeholder : stats.lastSoldPriceLabel}
            </span>
            <span className={styles.lastSoldPriceCompact}>
              {isComplianceBlocked ? placeholder : stats.lastSoldPriceCompactLabel}
            </span>
          </p>
        </div>
        <div className={styles.statIcon}>
          <TrendingUpIcon size={48} bgColor="#F8FBFD" iconColor="#187CB8" sx={{ borderRadius: '100%' }} />
        </div>
      </article>
      <article className={styles.statCard}>
        <div className={styles.statContent}>
          <p className={styles.statLabel}>
            {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_STAT_APPRECIATION, { defaultValue: 'Appreciation' })}
          </p>
          <p className={styles.statValue}>{isComplianceBlocked ? placeholder : stats.appreciationLabel}</p>
        </div>
        <div className={styles.statIcon}>
          <HomeWorkIcon size={48} bgColor="#F4F0EA" iconColor="#91682B" sx={{ borderRadius: '100%' }} />
        </div>
      </article>
      <article className={styles.statCard}>
        <div className={styles.statContent}>
          <p className={styles.statLabel}>
            {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_STAT_AVG_DAYS_LISTED, { defaultValue: 'Avg. Days Listed' })}
          </p>
          <p className={styles.statValue}>{isComplianceBlocked ? placeholder : stats.averageDaysListedLabel}</p>
        </div>
        <div className={styles.statIcon}>
          <EventRepeatIcon size={48} bgColor="#F2F2F2" iconColor="#303030" sx={{ borderRadius: '100%' }} />
        </div>
      </article>
    </div>
  );
}

function getStatusTitleClass(status: StandardStatus): string {
  if (status === StandardStatus.STANDARD_STATUS_CLOSED) return styles.soldTitle;
  if (isActive(status)) return styles.activeTitle;
  return styles.neutralTitle;
}

// Compact one-line row for older records shown beneath the group's latest card.
function TimelineSecondaryRecord({ record, onViewListingClick }: {
  record: PriceHistoryTimelineRecordView;
  onViewListingClick?: PriceHistoryViewListingHandler;
}) {
  const statusClass = getStatusTitleClass(record.status);
  const defaultLine = '{{date}}, {{status}} for <price>{{price}}</price>';
  const { isClickable, handleClick } = usePriceHistoryRecordNavigation(record, onViewListingClick);

  return (
    <li
      className={`${styles.miniEntry} ${isClickable ? styles.clickableMiniEntry : ''}`}
      onClick={handleClick}
      onKeyDown={event => {
        if (!isClickable) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <span className={styles.miniEntryDot} aria-hidden="true" />
      <p className={styles.miniEntryText}>
        <Trans
          i18nKey={LOCALE_COMMON_PATHS.PRICE_HISTORY_LINE_STATUS_FOR}
          values={{
            date: record.absoluteDateLabel,
            status: record.statusLabel,
            price: record.priceLabel,
          }}
          defaultValue={defaultLine}
          components={{
            price: <span className={statusClass} />,
          }}
        />
      </p>
    </li>
  );
}

// Group section that renders the month header, latest record card, and any older records below it.
function PriceHistoryMonthGroup({ group, onViewListingClick }: {
  group: PriceHistoryTimelineMonthGroupView;
  onViewListingClick?: PriceHistoryViewListingHandler;
}) {
  const isTodayGroup = group.monthKey === 'today';
  const statusClass = getStatusTitleClass(group.titleStatus);

  return (
    <div className={styles.group}>
      <h3 className={styles.groupHeader}>
        {isTodayGroup ? (
          <Trans
            i18nKey={LOCALE_COMMON_PATHS.PRICE_HISTORY_TIMELINE_GROUP_TODAY_TITLE}
            values={{ status: group.titleStatusLabel, price: group.titlePriceLabel }}
            defaultValue={`Today, ${group.titleStatusLabel} for <price>${group.titlePriceLabel}</price>`}
            components={{
              price: <span className={statusClass} />,
            }}
          />
        ) : (
          <>
            {group.monthLabel}
            {group.monthLabel && ', '}
            <Trans
              i18nKey={LOCALE_COMMON_PATHS.PRICE_HISTORY_TIMELINE_HEADER_STATUS_FOR}
              values={{ status: group.titleStatusLabel, price: group.titlePriceLabel }}
              defaultValue={`${group.titleStatusLabel} for <price>${group.titlePriceLabel}</price>`}
              components={{
                price: <span className={statusClass} />,
              }}
            />
          </>
        )}
      </h3>
      <div className={styles.entries}>
        <PriceHistoryCard
          key={group.latestRecord.id ?? `${group.monthKey}-${group.latestRecord.absoluteDateLabel}-${group.latestRecord.priceLabel}`}
          record={group.latestRecord}
          onViewListingClick={onViewListingClick}
        />
        {group.records.length > 1 && (
          <ul className={styles.miniTimeline}>
            {group.records.slice(1).map(record => (
              <TimelineSecondaryRecord
                key={record.id ?? `${group.monthKey}-${record.absoluteDateLabel}-${record.priceLabel}`}
                record={record}
                onViewListingClick={onViewListingClick}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Bottom chip button that shares expand/collapse state with the header chevron.
function TimelineExpandControls({
  isExpanded,
  showExpandControls,
  onToggleExpanded,
}: {
  isExpanded: boolean;
  showExpandControls: boolean;
  onToggleExpanded: () => void;
}) {
  const { t } = useTranslation(Namespaces.COMMON);

  if (!showExpandControls) return null;

  return (
    <div className={isExpanded ? styles.expandChipContainer : styles.expandChipOverlay}>
      <PillCtaButton
        variant="secondary"
        size="md"
        onClick={onToggleExpanded}
        aria-label="Toggle price history expansion"
        sx={BOTTOM_EXPAND_BUTTON_SX}
      >
        <ChevronIcon
          width={BOTTOM_EXPAND_BUTTON_ICON_SIZE}
          height={BOTTOM_EXPAND_BUTTON_ICON_SIZE}
          isExpanded={isExpanded}
        />
        {isExpanded
          ? t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_LESS_BUTTON_LABEL, { defaultValue: 'View Less' })
          : t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_MORE_BUTTON_LABEL, { defaultValue: 'View More' })}
      </PillCtaButton>
    </div>
  );
}

// Main price history container that renders stats, grouped records, expand controls, and compliance gating.
export const PriceHistoryTimeline = forwardRef<HTMLDivElement, PriceHistoryTimelineProps>(
  function PriceHistoryTimeline(
    {
      currentListingId,
      saleHistory = [],
      rentalHistory = [],
      currentListingData,
      onComplianceRequirementClick,
      onViewListingClick,
    },
    ref
  ) {
    const historyWithoutCurrentListing = saleHistory
      .concat(rentalHistory)
      .filter(record => record.id !== currentListingId);
    const { activeModal } = useModal();
    const isInAddressModal = activeModal === ADDRESS_MODAL;
    const priceHistory = [currentListingData, ...historyWithoutCurrentListing];
    const onComplianceRequirementClickRef = useRef(onComplianceRequirementClick);
    // Match legacy behavior: compliance gating is driven by history rows only.
    const highestRequirement = getHighestComplianceRequirement(historyWithoutCurrentListing);
    const isComplianceBlocked = highestRequirement.requirement !== ComplianceRequirement.NONE;
    const statsValuePlaceholder = '--';
    const timeline = usePriceHistoryTimeline({
      records: priceHistory,
      currentListingId,
      // Compliance still shows two historical rows; add one more slot for the synthetic Today group.
      visibleCount: isComplianceBlocked
        ? COMPLIANCE_VISIBLE_ENTRY_COUNT + TODAY_GROUP_VISIBLE_COUNT
        : undefined,
    });

    useIsomorphicLayoutEffect(() => {
      onComplianceRequirementClickRef.current = onComplianceRequirementClick;
    }, [onComplianceRequirementClick]);

    const handleComplianceRequirementClick = useCallback(() => {
      onComplianceRequirementClickRef.current?.(highestRequirement.requirement, highestRequirement.providerId);
    }, [highestRequirement.requirement, highestRequirement.providerId]);

    if (!timeline.hasValue) return null;
    const showExpandControls = timeline.canExpand && !isComplianceBlocked;

    return (
      <div className={`${styles.component} ${isInAddressModal ? styles.modalView : ''}`} data-testid={listingPageModalIds.priceHistorySection} ref={ref}>
        <TimelineHeader
          isExpanded={timeline.isExpanded}
          showExpandControls={showExpandControls}
          onToggleExpanded={timeline.toggleExpanded}
        />

        <TimelineStats
          stats={timeline.stats}
          isComplianceBlocked={isComplianceBlocked}
          placeholder={statsValuePlaceholder}
        />

        <div className={styles.contentArea}>
          <div className={`${styles.content} ${showExpandControls && !timeline.isExpanded ? styles.collapsed : ''}`}>
            {timeline.visibleGroups.map(group => (
              <PriceHistoryMonthGroup
                key={group.monthKey}
                group={group}
                onViewListingClick={onViewListingClick}
              />
            ))}
          </div>
          {showExpandControls && !timeline.isExpanded && <div className={styles.collapsedOverlay} aria-hidden="true" />}
          <TimelineExpandControls
            isExpanded={timeline.isExpanded}
            showExpandControls={showExpandControls}
            onToggleExpanded={timeline.toggleExpanded}
          />
        </div>

        {isComplianceBlocked && (
          <ComplianceRequirementMessage
            complianceRequirement={highestRequirement.requirement}
            onClick={handleComplianceRequirementClick}
          />
        )}
      </div>
    );
  }
);

PriceHistoryTimeline.displayName = 'PriceHistoryTimeline';
